/**
 * Phase 19.36 — Dashboard discrepancy alert badge.
 *
 * Calls /api/quotes/discrepancies on mount; if any quotes are flagged
 * (>=20% movement vs the same client's prior quote), shows a compact
 * amber pill on the owner's dashboard with a one-click link to Mass Quote
 * Update. Silent (renders nothing) if the user lacks permission or if
 * nothing is flagged. Cheap safety net to catch typos & mispriced re-quotes
 * before they reach the customer.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const THRESHOLD = 20;

export default function DiscrepancyAlert() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/quotes/discrepancies?threshold_percent=${THRESHOLD}`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setCount(data.count || 0);
        setItems((data.discrepancies || []).slice(0, 3));
      })
      .catch(() => {
        // 403 (not an internal user) or 5xx — silently hide.
        if (!cancelled) {
          setCount(0);
          setItems([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (count === 0 || hidden) return null;

  return (
    <div
      data-testid="dashboard-discrepancy-alert"
      className="mb-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 flex items-start gap-3"
    >
      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
          {count} {count === 1 ? "quote needs" : "quotes need"} review
        </p>
        <p className="text-xs text-amber-800/80 dark:text-amber-200/80 mt-0.5">
          {count === 1 ? "A client's" : "Some clients'"} latest quote moved more than {THRESHOLD}% from their previous one. Catch typos & mispriced re-quotes before they send.
        </p>
        {items.length > 0 && (
          <ul className="mt-2 space-y-0.5 text-xs text-amber-900 dark:text-amber-100/90">
            {items.map((d) => (
              <li key={d.current_quote_id} className="truncate">
                • <strong>{d.client_name}</strong> — {d.previous_quote_number} → {d.current_quote_number}
                <span className={`ml-1 font-mono ${d.percentage_diff > 0 ? "text-rose-700 dark:text-rose-300" : "text-emerald-700 dark:text-emerald-300"}`}>
                  {d.percentage_diff > 0 ? "+" : ""}{d.percentage_diff}%
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2 mt-2">
          <Link
            to="/quotes"
            data-testid="discrepancy-review-link"
            className="text-xs font-medium px-2 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white"
          >
            Review now
          </Link>
          <button
            onClick={() => setHidden(true)}
            data-testid="discrepancy-dismiss-btn"
            className="text-xs font-medium px-2 py-1 rounded border border-amber-400 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/40"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
